import {
  useRef,
  useCallback,
  useState,
  useLayoutEffect,
  Children
} from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { PropTypes } from 'prop-types';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';

import { dataTrackingPropsType } from '../../../utils/reporting';

const SimpleStepIcon = () => (
  <Box
    sx={{
      width: '25px',
      height: '4px'
    }}
  />
);

const HorizontalCarousel = ({ children, dataTrackingProps }) => {
  const rootRef = useRef(null); /* reference of the scrollable container (root) element */
  const itemsRef = useRef(null);
  const stepsRef = useRef(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [lastItemMargin, setLastItemMargin] = useState(0);

  const trackNextClick = useButtonPressedCallback({
    ...dataTrackingProps,
    elementName: 'carousel-next',
    elementAsset: 'KeyboardArrowRightIcon'
  });

  const trackBackClick = useButtonPressedCallback({
    ...dataTrackingProps,
    elementName: 'carousel-back',
    elementAsset: 'KeyboardArrowLeftIcon'
  });

  const trackSelectClick = useButtonPressedCallback({
    ...dataTrackingProps,
    elementName: 'carousel-select',
    elementAsset: 'SimpleStepIcon'
  });

  const getItems = () => {
    if (!itemsRef.current) {
      itemsRef.current = new Map();
    }
    return itemsRef.current;
  };

  const getSteps = () => {
    if (!stepsRef.current) {
      stepsRef.current = new Map();
    }
    return stepsRef.current;
  };

  useLayoutEffect(() => {
    if (typeof window.ResizeObserver === 'undefined') {
      return; /* Not supported */
    }

    const handleResize = (entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        const lastItem = Array.from(rootRef.current.children).at(-1);
        const itemWidth = lastItem?.getBoundingClientRect().width || 0;
        setLastItemMargin(itemWidth > containerWidth ? 0 : containerWidth - itemWidth);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(rootRef.current);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window.IntersectionObserver === 'undefined') {
      return; /* Not supported */
    }

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const currentIndex = Array.from(rootRef.current.children).indexOf(
            entry.target
          );
          setActiveStepIndex(currentIndex);
        }
      });
    };

    const intersectionObserver = new IntersectionObserver(handleIntersection, {
      root: rootRef.current,
      rootMargin: `0px -${lastItemMargin}px 0px 0px`,
      threshold: 0.85
    });

    // observe each child element
    for (const item of getItems().values()) {
      intersectionObserver.observe(item);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      intersectionObserver.disconnect();
    };
  }, [lastItemMargin]);

  const elementCallbackRef = useCallback((elements, node, index) => {
    if (!node) {
      elements.delete(index);
      return;
    }

    elements.set(index, node);
  }, []);

  function scrollToIndex(index) {
    const items = getItems();
    const node = items.get(index);

    // scroll on next tick so the scroll functionality does not get interrupted
    setTimeout(() => {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest' /* "nearest" to help avoid vertical scroll */,
        inline:
          'start' /* "start" horizontal scroll to the beginning of the container element */
      });
    });
  }

  const handleSelect = (index, e) => {
    trackSelectClick(e);
    setActiveStepIndex(index);
    scrollToIndex(index);
  };

  const childrenCount = Children.count(children);
  if (!childrenCount) {
    return null;
  }

  const handleNext = (e) => {
    trackNextClick(e);
    setActiveStepIndex((prev) => {
      let index = prev + 1;
      if (index > childrenCount - 1) {
        index = childrenCount - 1;
      }

      scrollToIndex(index);
      return index;
    });
  };

  const handleBack = (e) => {
    trackBackClick(e);
    setActiveStepIndex((prev) => {
      let index = prev - 1;
      if (index < 0) {
        index = 0;
      }

      scrollToIndex(index);
      return index;
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box
        component="ul"
        ref={rootRef}
        sx={{
          margin: 0,
          padding: 0,
          display: 'flex',
          gap: 4,
          overflowX: 'scroll',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          '& > *': {
            scrollSnapAlign: 'start'
          },
          '& > *:last-of-type': {
            /* Provide enough room for the last item to scroll to the beginning of the container */
            marginRight: lastItemMargin
          },
          '::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {Children.map(children, (child, index) => (
          <Box
            ref={(node) => elementCallbackRef(getItems(), node, index)}
            component="li"
            display="inline-block"
          >
            {child}
          </Box>
        ))}
      </Box>
      <Box display="flex" justifyContent="center">
        <IconButton
          size="small"
          onClick={handleBack}
          disabled={activeStepIndex <= 0}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <Stepper
          connector={null}
          nonLinear
          sx={{
            maxWidth: '100%',
            overflow: 'auto'
          }}
        >
          {Children.map(children, (_, index) => (
            <Step
              active={index === activeStepIndex}
              ref={(node) => elementCallbackRef(getSteps(), node, index)}
              onClick={(e) => handleSelect(index, e)}
              sx={{
                '&.MuiStep-root': {
                  padding: 0
                },

                '& .MuiStepLabel-iconContainer': {
                  px: 0,
                  mx: 0.5,
                  backgroundColor: 'focus30.light',
                  borderRadius: '100px',
                  cursor: 'pointer',

                  '&.Mui-active': {
                    backgroundColor: 'focus60.light'
                  }
                }
              }}
            >
              <StepLabel StepIconComponent={SimpleStepIcon} />
            </Step>
          ))}
        </Stepper>
        <IconButton
          size="small"
          onClick={handleNext}
          disabled={activeStepIndex >= childrenCount - 1}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

HorizontalCarousel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.element,
    PropTypes.node
  ]).isRequired,
  dataTrackingProps: dataTrackingPropsType
};

export { HorizontalCarousel };
